import React, { useState, useEffect } from 'react';
import { Layout, Card, Table, Button, Modal, Form, Input, Select, message, Space, Typography, DatePicker, Row, Col, InputNumber } from 'antd';
import { PlusOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const TransfersPage = () => {
    const [transfers, setTransfers] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchTransfers();
        fetchBankAccounts();
    }, []);

    const fetchTransfers = async () => {
        setLoading(true);
        try {
            // We don't have a specific GET /transfers yet, but we can fetch from JVs or create a new endpoint
            // For now, let's assume we fetch from a new endpoint or just show a message
            const res = await api.get('/api/accounting/journal-entries'); // Placeholder
            if (res.data.success) {
                // Filter JVs that start with 'TR' (Transfers)
                setTransfers(res.data.data.filter(jv => jv.jv_number.startsWith('TR')));
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลการโอนเงินได้');
        } finally {
            setLoading(false);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const res = await api.get('/api/accounting/bank-accounts');
            if (res.data.success) {
                setBankAccounts(res.data.data);
            }
        } catch (error) { }
    };

    const handleSubmit = async (values) => {
        try {
            await api.post('/api/accounting/transfers', {
                ...values,
                transfer_date: values.transfer_date.format('YYYY-MM-DD')
            });
            message.success('โอนเงินและบันทึกบัญชีเรียบร้อย');
            setIsModalVisible(false);
            form.resetFields();
            fetchTransfers();
        } catch (error) {
            message.error(error.response?.data?.message || 'ไม่สามารถทำรายการได้');
        }
    };

    const columns = [
        { title: 'เลขที่รายการ', dataIndex: 'jv_number', key: 'jv_number' },
        { title: 'วันที่', dataIndex: 'entry_date', key: 'entry_date', render: (date) => dayjs(date).format('DD/MM/YYYY') },
        { title: 'รายละเอียด', dataIndex: 'description', key: 'description' },
        { title: 'อ้างอิง', dataIndex: 'reference_no', key: 'reference_no' }
    ];

    return (
        <Layout style={{ padding: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={2}>โอนเงินระหว่างบัญชี (Bank Transfers)</Title>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchTransfers}>รีเฟรช</Button>
                        <Button type="primary" icon={<SwapOutlined />} onClick={() => {
                            form.resetFields();
                            form.setFieldsValue({ transfer_date: dayjs() });
                            setIsModalVisible(true);
                        }}>โอนเงินใหม่</Button>
                    </Space>
                </div>

                <Card>
                    <Table
                        columns={columns}
                        dataSource={transfers}
                        rowKey="id"
                        loading={loading}
                    />
                </Card>
            </Space>

            <Modal
                title="ทำรายการโอนเงินระหว่างบัญชี"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                okText="ยืนยันการโอน"
                cancelText="ยกเลิก"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="transfer_date" label="วันที่โอน" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="from_account_id" label="จากบัญชี" rules={[{ required: true }]}>
                                <Select placeholder="เลือกบัญชีต้นทาง">
                                    {bankAccounts.map(acc => (
                                        <Option key={acc.id} value={acc.id}>
                                            {acc.account_name} ({acc.bank_name})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="to_account_id" label="ไปยังบัญชี" rules={[{ required: true }]}>
                                <Select placeholder="เลือกบัญชีปลายทาง">
                                    {bankAccounts.map(acc => (
                                        <Option key={acc.id} value={acc.id}>
                                            {acc.account_name} ({acc.bank_name})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="amount" label="จำนวนเงิน" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={0.01} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="fee" label="ค่าธรรมเนียม (ถ้ามี)">
                                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="reference_no" label="เลขที่อ้างอิง">
                        <Input placeholder="เช่น เลขที่สลิป" />
                    </Form.Item>

                    <Form.Item name="notes" label="หมายเหตุ">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default TransfersPage;
